import ControlSlider from "./control-slider";
import { CONTROL_SLIDER_MARKER_PCTS } from "./slider-math";

// Normal: the slider runs 1 → 1.4 and maps to slowControl 1 → 0.6 (slower).
// With Fast Control on (its pill below), the same travel maps to 1 → 1.4, so the
// control speeds the video up instead. Either way the far end of the slider
// is "more effect", so the track and markers stay put.
//
// Pitch Lock, Fast Control and Reset sit right under the control they
// change, as pills, the way Slowed Studio shows them (its `control__options`
// row): Pitch Lock and Fast Control are Pro, so while Pro is locked they show
// locked and open the upgrade; Reset shows only while a control is off its
// default.
const ICONS = {
  pitchLock: (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
        clipRule="evenodd"
      />
    </svg>
  ),
  fastControl: (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M3 5.5a1 1 0 011.6-.8l5.4 4.5a1 1 0 010 1.6l-5.4 4.5A1 1 0 013 14.5v-9zm7.5 0a1 1 0 011.6-.8l5.4 4.5a1 1 0 010 1.6l-5.4 4.5a1 1 0 01-1.6-.8v-9z" />
    </svg>
  ),
  reset: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 10a6 6 0 1 0 1.8-4.3M4 3v3.5h3.5" />
    </svg>
  ),
};

function OptionPill({ icon, label, title, pressed, locked, onClick }) {
  const toggle = pressed !== undefined;
  return (
    <button
      type="button"
      className={`mood__chip content__option${
        pressed && !locked ? " mood__chip--selected" : ""
      }${locked ? " mood__chip--locked" : ""}`}
      title={title}
      aria-pressed={toggle ? !!pressed && !locked : undefined}
      aria-disabled={locked || undefined}
      onClick={() => onClick?.()}
    >
      {icon}
      <span className="mood__chip-name">{label}</span>
    </button>
  );
}

export default function SlowControlSection({
  slowControl,
  speedUpMode = false,
  pitchLocked = false,
  isProFeaturesLocked = false,
  showReset = false,
  onSlowControlChange,
  onPitchLockChange,
  onSpeedUpModeChange,
  onResetClick,
  onUpgradeClick,
}) {
  const locked = !!isProFeaturesLocked;
  // Never on while locked, even if it was left on before.
  const fast = !locked && !!speedUpMode;
  const pitch = !locked && !!pitchLocked;
  const stepLabel = slowControl === 1 ? "None" : String(slowControl);
  const sliderValue = fast ? slowControl : 2 - slowControl;

  return (
    <div className="content__item content__item--slow">
      {/* With Fast Control on, the control is named for what it does now. */}
      <div className="content__title">
        {fast ? "Fast Control" : "Slow Control"}
      </div>
      <ControlSlider
        label={stepLabel}
        markerPcts={CONTROL_SLIDER_MARKER_PCTS}
        min="1"
        max="1.4"
        step="0.01"
        value={sliderValue}
        ghostEnabled
        onChange={(event) => {
          const raw = parseFloat(event.currentTarget.value);
          const next = fast ? raw : 2 - raw;
          onSlowControlChange(parseFloat(next.toFixed(2)));
        }}
      />
      <div className="content__options">
        <OptionPill
          icon={ICONS.pitchLock}
          label="Pitch Lock"
          title={
            locked
              ? "Pitch Lock is a Pro feature"
              : pitch
                ? "The song keeps its own pitch at any speed"
                : "Keep the song's own pitch at any speed"
          }
          pressed={pitch}
          locked={locked}
          onClick={() =>
            locked ? onUpgradeClick?.() : onPitchLockChange?.(!pitch)
          }
        />
        <OptionPill
          icon={ICONS.fastControl}
          label="Fast Control"
          title={
            locked
              ? "Fast Control is a Pro feature"
              : fast
                ? "The control is speeding the song up"
                : "Turn Slow Control into a fast control"
          }
          pressed={fast}
          locked={locked}
          onClick={() =>
            locked ? onUpgradeClick?.() : onSpeedUpModeChange?.(!fast)
          }
        />
        {showReset && (
          <OptionPill
            icon={ICONS.reset}
            label="Reset"
            title="Put every control back to its default"
            onClick={() => onResetClick?.()}
          />
        )}
      </div>
    </div>
  );
}
